import React, { useEffect, useState } from 'react';
import { Row, Col, Typography } from 'antd';
import { MedicineBoxOutlined } from '@ant-design/icons';
import { cmsAPI, siteAPI } from '../services/api';
import { isExternalHref, unwrapSiteConfigValue } from '../utils/siteConfig';
import { useSiteBranding } from '../hooks/useSiteBranding';

const { Text } = Typography;

const NAVY = '#0B2545';
const GOLD = '#C5A55A';

interface ProductLink {
  title: string;
  href: string;
  sort_order?: number;
}

interface FooterSection {
  name: string;
  pages: Array<{
    title: string;
    slug: string;
    is_external?: boolean;
    external_url?: string;
  }>;
}

const PageFooter: React.FC = () => {
  const [sections, setSections] = useState<FooterSection[]>([]);
  const [products, setProducts] = useState<ProductLink[]>([]);
  const [icpNumber, setIcpNumber] = useState('');
  const [logoFailed, setLogoFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const {
    siteName,
    slogan: tagline,
    copyright: copyrightText,
    landingFooterLogoUrl,
    loading: brandingLoading,
  } = useSiteBranding();

  useEffect(() => {
    setLogoFailed(false);
  }, [landingFooterLogoUrl]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      const [sectionsResult, productsResult, icpResult] = await Promise.allSettled([
        cmsAPI.getSections(),
        siteAPI.getConfig('footer_products'),
        siteAPI.getConfig('icp_number'),
      ]);

      setSections(
        sectionsResult.status === 'fulfilled' && Array.isArray(sectionsResult.value)
          ? sectionsResult.value
          : [],
      );

      if (productsResult.status === 'fulfilled' && Array.isArray(productsResult.value?.value)) {
        const productLinks = [...productsResult.value.value].sort(
          (a: ProductLink, b: ProductLink) => (a.sort_order || 0) - (b.sort_order || 0),
        );
        setProducts(productLinks);
      } else {
        setProducts([]);
      }

      if (icpResult.status === 'fulfilled') {
        const value = unwrapSiteConfigValue<unknown>(icpResult.value?.value);
        setIcpNumber(typeof value === 'string' ? value.trim() : '');
      } else {
        setIcpNumber('');
      }

      setLoading(false);
    };

    loadData().catch((error) => {
      console.error('Failed to load footer data:', error);
      setLoading(false);
    });
  }, []);

  if (loading || brandingLoading) return null;


  return (
    <footer
      style={{
        background: NAVY,
        padding: '48px 0 24px',
        borderTop: `3px solid ${GOLD}`,
      }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <Row gutter={[48, 32]}>
          <Col xs={24} md={8}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16,
              }}
            >
              {!logoFailed ? (
                <img
                  src={landingFooterLogoUrl}
                  alt={siteName}
                  style={{ maxHeight: 34, maxWidth: 180, objectFit: 'contain', display: 'block' }}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <>
                  <MedicineBoxOutlined style={{ fontSize: 24, color: '#fff' }} />
                  <span style={{ fontSize: 18, fontWeight: 700, color: '#fff' }}>
                    {siteName}
                  </span>
                </>
              )}
            </div>
            <Text
              style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 13,
                lineHeight: 1.7,
              }}
            >
              {tagline}
            </Text>
          </Col>

          <Col xs={12} sm={8} md={5}>
            <div
              style={{
                color: GOLD,
                fontWeight: 600,
                fontSize: 14,
                marginBottom: 16,
              }}
            >
              产品
            </div>
            {products.map((product) => (
              <div key={`${product.title}-${product.href}`} style={{ marginBottom: 8 }}>
                <a
                  style={{
                    color: 'rgba(255,255,255,0.55)',
                    fontSize: 13,
                    textDecoration: 'none',
                  }}
                  href={product.href}
                  target={isExternalHref(product.href) ? '_blank' : undefined}
                  rel={isExternalHref(product.href) ? 'noopener noreferrer' : undefined}
                >
                  {product.title}
                </a>
              </div>
            ))}
          </Col>

          {sections.slice(0, 2).map((section) => (
              <Col key={section.name} xs={12} sm={8} md={5}>
                <div
                  style={{
                    color: GOLD,
                    fontWeight: 600,
                    fontSize: 14,
                    marginBottom: 16,
                  }}
                >
                  {section.name}
                </div>
                {section.pages.map((page) => {
                  const href = page.is_external ? page.external_url || '#' : `/pages/${page.slug}`;
                  const external = page.is_external || isExternalHref(href);

                  return (
                    <div key={`${section.name}-${page.slug}-${page.title}`} style={{ marginBottom: 8 }}>
                      <a
                        style={{
                          color: 'rgba(255,255,255,0.55)',
                          fontSize: 13,
                          textDecoration: 'none',
                        }}
                        href={href}
                        target={external ? '_blank' : undefined}
                        rel={external ? 'noopener noreferrer' : undefined}
                      >
                        {page.title}
                      </a>
                    </div>
                  );
                })}
              </Col>
            ))}
        </Row>

        <div
          style={{
            marginTop: 40,
            paddingTop: 20,
            borderTop: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center',
          }}
        >
          <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
            {copyrightText}
          </Text>
          {icpNumber && (
            <div style={{ marginTop: 8 }}>
              <a
                href="https://beian.miit.gov.cn/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}
              >
                {icpNumber}
              </a>
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default PageFooter;
